/**
 * 建立多階層工單：一列選定一種實際料（不拆多列、不分攤量）。
 * BOM 需求料（mtr*）不變；實際選料寫入 selected*。
 * draft-plan 替代料：寫入 bomMaterialSelections[]。
 */

/** 倉別量／製作中：有值顯示數字、null／undefined 顯示 — */
export function formatManufacturingMaterialOptionalQty(value) {
  if (value == null || value === '') {
    return '—';
  }
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return '—';
  }
  return String(num);
}

/**
 * 是否需手選替代料／實際料（多組 group 或 legacy 多 candidates）。
 */
export function rowNeedsManufacturingMaterialSelection(row) {
  if (Array.isArray(row?.groupMtrOptions) && row.groupMtrOptions.length > 0) {
    return row.groupMtrOptions.some((group) => (group.options?.length ?? 0) > 0);
  }
  return Array.isArray(row?.candidates) && row.candidates.length > 1;
}

/**
 * 需選料的列是否已選齊（group 每組有 selectedMaterialId；legacy 有 selectedMtrId）。
 */
export function isManufacturingMaterialSelectionComplete(row) {
  const groupOptions = row?.groupMtrOptions ?? [];
  if (groupOptions.length > 0) {
    const selections = row?.bomMaterialSelections ?? [];
    return groupOptions.every((group) => {
      const selected = selections.find(
        (item) => item.sourceGroupMaterialId === group.sourceGroupMaterialId,
      );
      return Boolean(selected?.selectedMaterialId);
    });
  }
  return Boolean(row?.selectedMtrId);
}

/**
 * 已勾選且需選料、但尚未選齊的列。
 */
export function findSelectedRowsMissingMaterialSelection(rows = []) {
  return (rows ?? []).filter((row) => (
    row?.selected
    && rowNeedsManufacturingMaterialSelection(row)
    && !isManufacturingMaterialSelectionComplete(row)
  ));
}

/**
 * @param {object[]} rows
 * @param {{ rowId: string, material: { mtrId: string, mtrNo: string, mtrName: string } }} params
 * @returns {{ ok: true, rows: object[] }|{ ok: false, message: string }}
 */
export function applyManufacturingMaterialSelection(rows, { rowId, material }) {
  const list = Array.isArray(rows) ? rows : [];
  const index = list.findIndex((row) => row?.id === rowId);
  if (index < 0) {
    return { ok: false, message: '找不到需求列' };
  }
  if (!material?.mtrId && !material?.mtrNo) {
    return { ok: false, message: '請選擇實際料' };
  }

  const next = list.map((row) => ({ ...row }));
  next[index] = {
    ...next[index],
    selectedMtrId: material.mtrId ?? next[index].selectedMtrId,
    selectedMtrNo: material.mtrNo,
    selectedMtrName: material.mtrName,
  };
  return { ok: true, rows: next };
}

/**
 * draft-plan：一列寫入多組替代料選取。
 * @param {object[]} rows
 * @param {{
 *   rowId: string,
 *   selections: {
 *     sourceGroupMaterialId: string,
 *     selectedMaterialId: string,
 *     selectedMtrNo?: string,
 *     selectedMtrName?: string,
 *   }[],
 * }} params
 */
export function applyDraftPlanGroupSelections(rows, { rowId, selections }) {
  const list = Array.isArray(rows) ? rows : [];
  const index = list.findIndex((row) => row?.id === rowId);
  if (index < 0) {
    return { ok: false, message: '找不到需求列' };
  }
  const nextSelections = Array.isArray(selections) ? selections : [];
  if (nextSelections.some((item) => !item?.sourceGroupMaterialId || !item?.selectedMaterialId)) {
    return { ok: false, message: '請完成所有替代料選取' };
  }

  const primary = nextSelections[0];
  const next = list.map((row) => ({ ...row }));
  next[index] = {
    ...next[index],
    bomMaterialSelections: nextSelections.map((item) => ({
      sourceGroupMaterialId: item.sourceGroupMaterialId,
      selectedMaterialId: item.selectedMaterialId,
      selectedMtrNo: item.selectedMtrNo ?? '',
      selectedMtrName: item.selectedMtrName ?? '',
    })),
    selectedMtrId: primary?.selectedMaterialId,
    selectedMtrNo: primary?.selectedMtrNo,
    selectedMtrName: primary?.selectedMtrName,
  };
  return { ok: true, rows: next };
}
